export interface ServiceInfo {
  slug: string;
  name: string;
  short: string;
  h1: string;
  seoTitle: string;
  metaDescription: string;
  heroImage: string;
  cardImage: string;
  gallery?: { src: string; alt: string }[];
  benefits: string[];
  intro: string;
  sections: { heading: string; body: string }[];
  includes: string[];
  faqs: { q: string; a: string }[];
  cta: string;
  related: string[];
}

const serviceCatalog: ServiceInfo[] = [
  {
    slug: "dumpster-rental",
    name: "Dumpster Rental",
    short: "Dumpster Rental",
    h1: "Reliable Dumpster Rental in Highland, Utah",
    seoTitle: "Reliable Dumpster Rental in Highland, UT | Free Estimates",
    metaDescription:
      "Looking for dumpster rental in Highland, UT? Scotty's Land Specialist offers dumpster rental services for cleanups and property projects. Get a quote.",
    heroImage:
      "https://vibe.filesafe.space/1792592081893764394/assets/9b2fdaa3-ea56-4326-818a-9418925079f7.png",
    cardImage:
      "https://vibe.filesafe.space/1792592081893764394/assets/9b2fdaa3-ea56-4326-818a-9418925079f7.png",
    benefits: [
      "Convenient drop-off & pickup",
      "Ideal for cleanouts & renovations",
      "One local contact for the whole job",
      "Keep debris contained & tidy",
    ],
    intro:
      "Tackling a cleanup, remodel, or property project in Highland? Scotty's Land Specialist offers dumpster rental to help you collect debris in one place. Highland lots are large, and a weekly trash can does not hold a remodel pile or the oak brush that comes off them. The dumpster stays on the property for 24 hours.",
    sections: [
      {
        heading: "Convenient Dumpster Rentals for Property Cleanups",
        body: "A dumpster on-site makes property cleanups far more manageable. Highland lots are large, and a weekly trash can does not hold a remodel pile, a garage cleanout, or the oak brush that comes off them. Instead of making repeated trips to the dump, you load the debris and Scott hauls it away. The container stays on the property for 24 hours, then Scott picks it up.",
      },
      {
        heading: "Dumpster Rentals for Home and Business Projects",
        body: "Whether you are cleaning out a home, clearing a yard, or managing debris from a renovation, a rental container gives you a single place to collect material. Homes and small businesses around Highland use the same setup: the dumpster sits where the work is happening, and you are not stacking piles in the driveway while you wait on a hauler. Scott can help you plan the right setup for the project, including where it should sit so it does not block the drive. Tell the team what the project is and they will match the container to the load.",
      },
      {
        heading: "Simplify Your Next Cleanup",
        body: "Renting a dumpster takes the hassle out of debris disposal. Scott delivers the container, you have 24 hours to fill it, and he hauls it off. You talk with the team about what is going in, instead of working through a call center.",
      },
      {
        heading: "Request Dumpster Rental Pricing",
        body: "Pricing depends on the container and the type of material. The rental is 24 hours. A yard of brush is a different load than a kitchen demo, and Scott prices it from a plain description of the project. Call or text him with what you are tossing. He will give you straightforward rental options, with no package you have to decode. A few photos of the pile are usually enough for him to price it.",
      },
      {
        heading: "Dumpster Rentals in Highland and Nearby Communities",
        body: "Scotty's provides dumpster rental for Highland, Lehi, American Fork, Pleasant Grove, Alpine, and surrounding Utah County areas. Scott is based in Highland, so drop-off and pickup stay local. Reach out to check availability for your location before you schedule the demo or the cleanup. If the address is a little farther out, the team will tell you whether they can get a container there. Text the address and what you are throwing out, and they will confirm the drop-off.",
      },
    ],
    includes: [
      "Container delivery & pickup",
      "Property cleanup projects",
      "Renovation & remodel debris",
      "Yard waste collection",
      "24-hour rental",
      "Local drop-off service",
    ],
    faqs: [
      {
        q: "What sizes do you offer?",
        a: "Container options depend on availability and your project. Call us with a description of what you're tossing and we'll recommend the right fit.",
      },
      {
        q: "How long can I keep the dumpster?",
        a: "The dumpster stays for 24 hours. Scott delivers it, you load it, and he picks it up the next day.",
      },
      {
        q: "What can I put in the dumpster?",
        a: "Most common household, yard, and construction debris is accepted. Scott can confirm any material restrictions when you book.",
      },
    ],
    cta: "Request a Dumpster Rental Quote",
    related: ["land-clearing", "spring-cleanup"],
  },
  {
    slug: "land-clearing",
    name: "Land Clearing",
    short: "Land Clearing",
    h1: "Reliable Land Clearing in Highland, Utah",
    seoTitle: "Reliable Land Clearing in Highland, UT | Free Estimates",
    metaDescription:
      "Reclaim overgrown land in Highland, Utah. Scotty's Land Specialist provides land clearing and vegetation cleanup for property projects. Request a quote.",
    heroImage:
      "https://vibe.filesafe.space/1792592081893764394/assets/8543560d-0ee7-4454-a685-70c564ff6f39.png",
    cardImage:
      "https://vibe.filesafe.space/1792592081893764394/assets/cf4ec8bd-f69b-44bd-a658-286b12fad284.png",
    benefits: [
      "Clear brush & overgrowth",
      "Reclaim usable outdoor space",
      "Prepare lots for their next use",
      "Local, dependable crew",
    ],
    intro:
      "Overgrown brush and vegetation can make a property feel unusable. Scotty's Land Specialist helps property owners in Highland and nearby Utah County communities clear overgrowth and reclaim space that's ready for its next step.",
    sections: [
      {
        heading: "Reclaim Your Overgrown Property",
        body: "Whether a lot has been left to grow wild or you are preparing a site for a new use, clearing the overgrowth is the first move. Brush closes in on fences, drives, and the parts of a Highland lot nobody has walked in years. Scotty's handles the brush, weeds, and small growth so the land is open and accessible again. You can see the ground, get equipment in, and decide what the property is actually for. Scott walks the lot first so the clearing matches what has actually grown in.",
      },
      {
        heading: "Brush and Vegetation Clearing",
        body: "We clear brush, weeds, and unwanted vegetation from residential lots and larger acreage. On the east side of Highland, toward American Fork Canyon, Gambel oak resprouts from an underground lignotuber, so cutting the trunk is not enough. The roots and stumps come out so the ground can actually be used. A surface cut looks finished for a season and then the same thicket is back. Scott walks the lot and takes out what will come straight back if it is only trimmed. You will know before the work starts whether the roots are coming out.",
      },
      {
        heading: "Property Preparation and Cleanup",
        body: "Land clearing is often part of a bigger property plan. The brush comes off, then the debris has to leave, or the lot is just a different kind of mess. Scott can help with the clearing and the cleanup that gets your lot ready for landscaping, building, or simply better use. If the pile needs a dumpster or a haul-off, that can be part of the same job instead of a second contractor. Scott loads what came off the lot and gets it gone.",
      },
      {
        heading: "Land Clearing for Usable Outdoor Space",
        body: "A cleared property is a usable property. Once the overgrowth is gone, you can see the slope, the access, and what is worth keeping. That is when a plan for the next phase actually makes sense, whether the lot is going to be lived on, fenced, or just kept open. Scott's work is the clearing itself: brush, small growth, and the roots that would undo a quick cut. You do not have to clear every square foot. Tell the team which parts you use.",
      },
      {
        heading: "Serving Highland and Surrounding Utah County",
        body: "Based in Highland, Scotty's provides land clearing for Highland, Lehi, American Fork, Pleasant Grove, Alpine, and surrounding areas. Canyon-side lots and older acreage take a different pass than a flat suburban yard, and Scott knows which is which because he works these neighborhoods. Call or text to schedule a walk-through. He will look at the growth and tell you what the clearing involves before any work starts. Give him the address and a short note about what has taken over the lot.",
      },
    ],
    includes: [
      "Brush & overgrowth clearing",
      "Weed & vegetation removal",
      "Small tree & shrub clearing",
      "Lot cleanup & debris hauling",
      "Property access improvement",
      "Site preparation support",
    ],
    faqs: [
      {
        q: "Can you clear large or overgrown lots?",
        a: "Yes. We handle overgrown residential lots and larger acreage. Describe the property and the team will let you know what's involved.",
      },
      {
        q: "Do you remove trees and stumps?",
        a: "We clear brush, weeds, and small growth as part of land clearing. For larger tree or stump work, talk with the team about your specific needs.",
      },
      {
        q: "What happens to the debris after clearing?",
        a: "Cleared material can be hauled away or managed on-site depending on the project. The team will review your options when you request a quote.",
      },
    ],
    cta: "Let's Clear Your Property",
    related: ["weed-control", "spring-cleanup"],
  },
  {
    slug: "weed-control",
    name: "Weed Control",
    short: "Weed Control",
    h1: "Reliable Weed Control in Highland, Utah",
    seoTitle: "Reliable Weed Control in Highland, UT | Free Estimates",
    metaDescription:
      "Need help with weeds and overgrowth in Highland, Utah? Scotty's Land Specialist provides seasonal weed control and property upkeep. Get a free quote.",
    heroImage:
      "https://vibe.filesafe.space/1792592081893764394/assets/82b55d1f-0b3b-4894-84c3-7c0100c44d2d.png",
    cardImage:
      "https://vibe.filesafe.space/1792592081893764394/assets/82b55d1f-0b3b-4894-84c3-7c0100c44d2d.png",
    benefits: [
      "Seasonal weed management",
      "Reduce overgrowth & clutter",
      "Cleaner, more usable property",
      "Local, practical approach",
    ],
    intro:
      "Weeds and overgrowth take over fast in Utah's growing seasons. Scotty's Land Specialist helps property owners in Highland and nearby communities stay ahead of weeds so outdoor areas stay cleaner and easier to use.",
    sections: [
      {
        heading: "Keep Unwanted Weeds Under Control",
        body: "Left alone, weeds crowd out the space you actually want to use. On a large Highland lot they take the edges, the open corners, and the ground you meant to keep clear. Scotty's helps bring overgrown areas back under control so the property looks better and works better. The point is usable ground, not a perfect show lawn. Some lots need one hard reset. Others only need the corners brought back.",
      },
      {
        heading: "Seasonal Weed Management",
        body: "Weed pressure changes with the seasons. Spring brings the first flush after the snow, summer fills in anything that was missed, and fall growth sets up next year's problem if it is left standing. Staying on top of it through the year keeps a property manageable. Scott can time a pass for the season you are in, or line weed control up with a spring or fall cleanup so the lot is handled once. Tell the team what the lot looks like and they will say whether this is the week to do it.",
      },
      {
        heading: "Weed and Overgrowth Removal",
        body: "We remove weeds and overgrowth from yards, lots, and larger properties. Field bindweed, goatheads, musk thistle, and Canada thistle get treated at the root, not just cut off at the top, so they do not come straight back. Bindweed roots run deep, and a quick cut at the surface is how the same patch returns in a few weeks. Scott walks the property, finds where the problem weeds are, and works the root system instead of mowing over them. The treatment follows the weed, not a one-size pass across the whole yard.",
      },
      {
        heading: "Maintain a Cleaner Property",
        body: "Regular weed control is one of the simplest ways to keep a property looking cared for. A lot that gets one hard reset and then nothing will look wild again by midsummer. Scott can help set up a practical approach that fits your lot, whether that is a single cleanup of a bad corner or a schedule through the growing season. You talk with the team about what you want kept open. Bindweed and thistle do not quit after one treatment, so the follow-up is part of keeping the ground clear.",
      },
      {
        heading: "Weed Control in Northern Utah County",
        body: "Scotty's provides weed control for Highland, Lehi, American Fork, Pleasant Grove, Alpine, and surrounding communities. On the larger lots farther out, weed control is often the whole job: pastures, open corners, and irrigation edges where noxious weeds take hold. Call or text the team to set up service. They will ask what is growing and come look before they recommend a plan. A photo of the patch and the address are enough to start.",
      },
    ],
    includes: [
      "Seasonal weed removal",
      "Overgrowth cleanup",
      "Yard & lot weed management",
      "Property upkeep support",
      "Clearing weedy areas",
      "Follow-up maintenance",
    ],
    faqs: [
      {
        q: "How often should weed control be done?",
        a: "It depends on the property and the season. The team can recommend a practical schedule based on your lot and goals.",
      },
      {
        q: "Do you use chemical treatments?",
        a: "We focus on practical weed and overgrowth removal. Talk with the team about your property and any preferences you have.",
      },
      {
        q: "Can you handle large overgrown areas?",
        a: "Yes. We work on both residential yards and larger lots. Describe the area and the team will plan the right approach.",
      },
    ],
    cta: "Get Help Controlling Your Weeds",
    related: ["land-clearing", "spring-cleanup", "fall-cleanup"],
  },
  {
    slug: "spring-cleanup",
    name: "Spring Cleanup",
    short: "Spring Cleanup",
    h1: "Reliable Spring Yard Cleanup & Seasonal Property Cleanup in Highland, Utah",
    seoTitle:
      "Reliable Spring Yard Cleanup & Seasonal Property Cleanup in Highland, UT | Free Estimates",
    metaDescription:
      "Get your Highland, Utah property ready for spring with yard cleanup, debris removal and seasonal property services from Scotty's. Request a quote.",
    heroImage:
      "https://vibe.filesafe.space/1792592081893764394/assets/e8ff3537-c538-41f0-907b-a19858debe50.png",
    cardImage:
      "https://vibe.filesafe.space/1792592081893764394/assets/e8ff3537-c538-41f0-907b-a19858debe50.png",
    benefits: [
      "Clear winter debris",
      "Remove early weeds & growth",
      "Open up outdoor space",
      "Get ready for the season",
    ],
    intro:
      "After a long Utah winter, properties need a reset. Scotty's Land Specialist helps homeowners and businesses in Highland and nearby communities clear away winter buildup and get outdoor spaces ready for the warmer months.",
    sections: [
      {
        heading: "Get Your Property Ready for Spring",
        body: "Spring cleanup is about shaking off the winter and opening your property back up. Months of snow, wind, and nobody walking the back of the lot leave a mess that a mower will not fix. Scotty's handles the debris, weeds, and clutter that pile up over the cold months. When the snow is gone, the yard should be usable again, not a layer of whatever winter dropped. On a large Highland lot, the back fence and the tree line hold most of that debris.",
      },
      {
        heading: "Remove Winter Debris and Seasonal Buildup",
        body: "Wind, snow, and inactivity leave yards full of branches, leaves, and debris. On a large Highland lot that also means gravel pushed onto the grass and the first weed flush after the snow melts. Leaves that sat under the snow turn into a wet mat, and broken limbs end up along the fence and the drive. We clear that buildup and haul it off so the property is clean and ready to use. Branches, the wet mat, and a corner full of debris go on the same visit.",
      },
      {
        heading: "Spring Weed and Overgrowth Cleanup",
        body: "Early weeds and new growth start fast in spring. The first warm stretch is when goatheads, thistle, and whatever was cut last year come back from the root. Getting ahead of them now keeps the property easier to manage through the rest of the year. Spring cleanup can include that first weed pass, so you are not booking a second visit a month later for growth that was already showing. The weeds that would reseed the lot get dealt with while they are still small.",
      },
      {
        heading: "Prepare Your Yard for the Warmer Months",
        body: "A good spring cleanup sets up the whole season. Once the debris and early weeds are gone, the outdoor space is ready for whatever you have planned, whether that is using the yard, fencing a section, or just keeping it open. Scott clears what winter left. He does not leave the piles sitting at the curb for you to deal with. Leaves and limbs come off the property on the same visit.",
      },
      {
        heading: "Schedule Your Spring Cleanup",
        body: "Based in Highland, Scotty's provides spring cleanup for Highland, Lehi, American Fork, Pleasant Grove, Alpine, and surrounding areas. Book early. Spring is the busiest season, and the lots that wait until the weeds are knee-high take longer. Call or text the team once the snow has cleared and they will get you on the schedule. Tell them the rough size of the lot and what winter left behind.",
      },
    ],
    includes: [
      "Winter debris removal",
      "Yard & lot cleanup",
      "Early weed cleanup",
      "Branch & leaf clearing",
      "Property refresh",
      "Debris hauling",
    ],
    faqs: [
      {
        q: "When should I schedule spring cleanup?",
        a: "Early spring is ideal, once the snow has cleared. Scotty's books up fast in spring, so reach out early to lock in a time.",
      },
      {
        q: "Do you haul away the debris?",
        a: "Yes. We collect and haul away the debris from your cleanup so your property is left clean.",
      },
      {
        q: "Can spring cleanup include weed control?",
        a: "Yes. Spring cleanup often pairs with early weed removal. Talk with the team about combining services for your property.",
      },
    ],
    cta: "Book Your Spring Cleanup",
    related: ["weed-control", "fall-cleanup"],
  },
  {
    slug: "fall-cleanup",
    name: "Fall Cleanup",
    short: "Fall Cleanup",
    h1: "Reliable Fall Cleanup in Highland, Utah",
    seoTitle: "Reliable Fall Cleanup in Highland, UT | Free Estimates",
    metaDescription:
      "Prepare your Highland, Utah property for winter with professional fall cleanup and seasonal debris removal from Scotty's Land Specialist.",
    heroImage: "/photos/scottyfallleaves.jpg",
    cardImage: "/photos/scottyfallleaves.jpg",
    benefits: [
      "Clear fallen leaves",
      "Remove late-season weeds",
      "Prep property for winter",
      "Keep access open & safe",
    ],
    intro:
      "Fall is the time to get your property winter-ready. Scotty's Land Specialist helps homeowners and businesses in Highland and nearby communities clear leaves, debris, and late-season growth before the snow arrives.",
    sections: [
      {
        heading: "Prepare Your Property for Winter",
        body: "A thorough fall cleanup protects your property through the cold months. Leaves, sticks, and late growth left on the ground get packed under the first real snow and stay there until spring. Clearing them now means less mess and fewer problems when winter hits. Fall cleanup runs through the middle of December, and into January when the snow holds off, so there is still time after the last big leaf drop. Canyon-side lots keep shedding after the valley yards look finished.",
      },
      {
        heading: "Fallen Leaves and Seasonal Yard Debris",
        body: "Leaves, branches, and yard debris pile up fast in fall. On a large Highland lawn, one weekend of raking does not finish it, and the piles left at the curb blow back across the grass. Left in place, they get packed under the snow and turn into a wet mat in the spring. Scott collects and hauls them away while the yard is still open, including the corners and the tree lines where the leaves stack the deepest. Branches that came down in the fall wind go with the same haul.",
      },
      {
        heading: "Fall Weed and Vegetation Cleanup",
        body: "Late-season weeds and overgrowth are easier to deal with now than in the spring. What is still green in November will seed and come back thicker if it stands all winter. A fall pass keeps that growth from getting a head start next year. Scott can pair the leaf haul with a weed pass so the lot goes into winter cleared, not half done. If both are heavy, they go together and you are not scheduling two trips.",
      },
      {
        heading: "Keep Outdoor Areas Clean and Accessible",
        body: "Clear paths, driveways, and yards are safer and easier to use through the winter. Leaves on a walk turn slick, and a drift of debris along the drive is one more thing the plow has to push. Fall cleanup keeps those areas open before the snow covers them. When snow removal starts, the entrance is a driveway, not a driveway buried in last month's yard waste. The front walk and the edges of the drive are part of the same cleanup.",
      },
      {
        heading: "Schedule Fall Cleanup in Utah County",
        body: "Based in Highland, Scotty's provides fall cleanup for Highland, Lehi, American Fork, Pleasant Grove, Alpine, and surrounding communities. The bench gets its leaves late, and the canyon-side lots drop even later. Schedule before the snow flies, or call if the snow has held off and the yard is still open. The team will tell you whether there is still a window to get it hauled. Photos of the yard help them put you on the schedule while the ground is still workable.",
      },
    ],
    includes: [
      "Leaf removal & hauling",
      "Yard debris cleanup",
      "Late weed cleanup",
      "Branch & stick clearing",
      "Winter prep",
      "Property access clearing",
    ],
    faqs: [
      {
        q: "When should fall cleanup be done?",
        a: "After most of the leaves have dropped but before the first heavy snow. Scott can help you time it right.",
      },
      {
        q: "Do you haul away the leaves and debris?",
        a: "Yes. We collect and haul away all the debris from your fall cleanup so your property is left clean.",
      },
      {
        q: "Can fall cleanup help with winter prep?",
        a: "Absolutely. Clearing debris and growth now makes winter easier and gives you a cleaner start in spring.",
      },
    ],
    cta: "Get Your Property Winter-Ready",
    related: ["snow-removal", "weed-control"],
  },
  {
    slug: "snow-removal",
    name: "Snow Removal",
    short: "Snow Removal",
    h1: "Reliable Snow Removal in Highland, Utah",
    seoTitle: "Reliable Snow Removal in Highland, UT | Free Estimates",
    metaDescription:
      "Need snow removal in Highland, Utah? Scotty's Land Specialist helps clear driveways, walkways and access areas. Request winter service pricing.",
    heroImage: "/photos/scottysnowremoval4.jpeg",
    cardImage: "/photos/scottysnowremoval4.jpeg",
    gallery: [
      {
        src: "/photos/scottysnowremoval1.jpeg",
        alt: "Scotty's snow removal trucks with plows parked in a snowy lot",
      },
      {
        src: "/photos/scottysnowremoval3.jpeg",
        alt: "Scotty's fleet of plow trucks lined up for snow removal",
      },
      {
        src: "/photos/scottysnowremoval2.jpeg",
        alt: "Snowblower and Scotty's SUV ready for a winter cleanup",
      },
    ],
    benefits: [
      "Driveway & walkway clearing",
      "Residential snow service",
      "Snow relocation when needed",
      "Keep access safe & open",
    ],
    intro:
      "Utah winters dump a lot of snow, and it has to go somewhere. Scotty's Land Specialist helps homeowners in Highland and nearby communities clear driveways, walkways, and access areas so properties stay safe and usable all winter.",
    sections: [
      {
        heading: "Residential Snow Removal Services",
        body: "From a fresh driveway clearing to ongoing winter service, Scotty's helps residential property owners stay on top of the snow. One storm can bury a Highland driveway overnight, and the next one stacks on what is already there. Scott can clear a single storm or set up service for the season so you are not calling around after every forecast. The driveway, the walk, and the way in stay open so you can get in and out safely. Seasonal service covers the early storms that hit before anyone is up.",
      },
      {
        heading: "Driveway and Walkway Snow Clearing",
        body: "Highland City plows streets, not driveways, and the plow often pushes a ridge back across the opening. That berm is usually the first thing that traps a car. Scott clears the driveway, the walk, and the sidewalk, including the ridge the city plow leaves behind. He does not plow city streets. If the property has a long drive or a private lane, he clears that access so the house is not cut off from the road. A short driveway and a long lane are different jobs, and he treats them that way.",
      },
      {
        heading: "Winter Property Access and Safety",
        body: "Snow and ice build up fast and create real hazards. A packed walk is slick by morning, and a buried driveway is a problem before anyone leaves for work. Keeping access areas clear protects your family, guests, and property all season. Scott works the surfaces people actually use: the drive, the front walk, the side path, and the way to the garage. The rest of the yard can stay under snow. The entrance cannot. Foot traffic packs the first inch into ice, and every later storm sits on top of that base.",
      },
      {
        heading: "Snow Plowing and Snow Relocation",
        body: "When snow piles up in the wrong places, it blocks access and creates problems. Pushing it to the side works until the banks get high enough to spill back onto the drive or bury a gate, a walk, or a parking spot. Scotty's can plow and relocate snowbanks so the property stays functional. That matters on larger Highland lots, where one storm's snow has to go somewhere that does not close off the next pass. By the third storm the edges are already banks.",
      },
      {
        heading: "Highland and Northern Utah County Snow Removal",
        body: "Based in Highland, Scotty's provides snow removal for Highland, Lehi, American Fork, Pleasant Grove, Alpine, and surrounding communities. Storms hit the bench and the canyon-side neighborhoods hard, and the city plow does not cover private property. Call or text the team ahead of the storm season to set up service. If a storm is already on the way, reach out and they will tell you what they can fit in. A text with the address and whether you need the drive, the walk, or both is enough to start.",
      },
    ],
    includes: [
      "Driveway plowing",
      "Walkway & sidewalk clearing",
      "Residential snow service",
      "Snowbank relocation",
      "Access road clearing",
      "Private lane service",
    ],
    faqs: [
      {
        q: "Do you offer seasonal snow service?",
        a: "Yes. Scott can set up ongoing residential snow service for the winter. Call to discuss your property and needs.",
      },
      {
        q: "Can you relocate snowbanks?",
        a: "Yes. When snow piles up in the wrong spot, we can plow and relocate it so your access stays clear.",
      },
      {
        q: "How do I schedule snow removal?",
        a: "Call or text the team ahead of the storm season to set up service. For same-storm needs, reach out and we'll let you know availability.",
      },
    ],
    cta: "Request Snow Removal Service",
    related: ["fall-cleanup", "land-clearing"],
  },
  {
    slug: "junk-removal",
    name: "Junk Removal",
    short: "Junk Removal",
    h1: "Reliable Junk Removal in Highland, Utah",
    seoTitle: "Reliable Junk Removal in Highland, UT | Free Estimates",
    metaDescription:
      "Need junk removal in Highland, UT? Scotty's Land Specialist loads and hauls garage, shed, and yard piles a weekly can won't take. Get a quote.",
    heroImage: "/photos/scottyjunkremoval2.jpeg",
    cardImage: "/photos/scottyjunkremoval2.jpeg",
    benefits: [
      "Load & haul garage and shed piles",
      "Furniture, brush & remodel debris",
      "One trip, not weeks of bagging",
      "Local, dependable crew",
    ],
    intro:
      "Large Highland lots collect garage, shed, and yard piles that a weekly trash can will not take. Scotty's Land Specialist loads and hauls furniture, brush, and remodel debris so the pile is actually gone.",
    sections: [
      {
        heading: "Junk Removal for Large Highland Lots",
        body: "A weekly trash can will not take old furniture, brush, or remodel debris. Large Highland lots collect those piles in the garage, the shed, and the side yard, and they sit there because there is room to ignore them. Scott loads and hauls what has built up so the space is actually clear. You do not spend a month of weekends bagging a pile that should have left in one trip. You point at the pile, Scott loads it, and the space is yours again. The lots around Highland are big enough that a pile can hide for years. One visit is how it leaves. You do not move it from the garage to the driveway so it can sit there instead. A weekly can is for household trash. This pile needs a truck.",
      },
      {
        heading: "Furniture, Brush, and Remodel Debris",
        body: "Scott takes the furniture, brush, and construction debris a weekly can cannot hold. Couches, mattresses, remodel scraps, and the brush from a cleanup all go on the same haul when they are part of one pile. One trip clears it instead of bagging a little at a time for months. Call with a plain description of what is there and the team will tell you what the pickup involves. Mixed piles are normal here: furniture next to brush next to leftover remodel material. If something in the pile is outside what the team takes, they will say so before they drive out. Couches, mattresses, brush, and remodel scraps can go on the same haul.",
      },
      {
        heading: "Garage, Shed, and Yard Cleanouts",
        body: "Moving junk from the garage to the shed just hides it. The pile is still on the property, and the next project has nowhere to go. Scott loads and hauls it all at once so the pile is actually gone. That includes the mixed piles people accumulate on a big lot: old furniture next to yard waste next to leftover remodel material. You do not have to sort it into a perfect stack before he shows up. Clear a path so he can get in, and he does the loading. Garages, sheds, and the open yard are the same job when the goal is to get the material off the property. If the pile is behind a gate or mixed with things you are keeping, say that when you book.",
      },
      {
        heading: "Junk Removal in Highland and Nearby Communities",
        body: "Based in Highland, Scotty's provides junk removal for Highland, Lehi, American Fork, Pleasant Grove, Alpine, and surrounding Utah County areas. Scott comes from Highland, loads the pile, and takes it off the property. Call or text to set up a pickup. Tell the team what is in the pile and where it is sitting, and they will give you a time. Photos make the quote faster. If the address is a little outside the usual towns, ask before you drag everything to the curb. They will say whether they can make the pickup. Call or text (801) 400-3346 and tell them whether the pile is in the garage, the shed, or the yard.",
      },
    ],
    includes: [
      "Furniture loading & hauling",
      "Brush & yard waste removal",
      "Remodel & construction debris",
      "Garage & shed cleanouts",
      "Yard pile removal",
      "One-trip hauling",
    ],
    faqs: [
      {
        q: "What can you haul away?",
        a: "Furniture, brush, yard waste, and remodel debris from garages, sheds, and yards. Call the team with a description of the pile and we'll let you know what's involved.",
      },
      {
        q: "Do I need to load it myself?",
        a: "No. Scott loads the junk so you don't have to, then hauls it off the property.",
      },
      {
        q: "How do I schedule junk removal?",
        a: "Call or text the team to describe the pile and set up a pickup time.",
      },
    ],
    cta: "Get That Pile Hauled Away",
    related: ["dumpster-rental", "land-clearing"],
  },
];

const seasonFirst = ["snow-removal", "fall-cleanup"];

export const services: ServiceInfo[] = [
  ...seasonFirst.map((slug) =>
    serviceCatalog.find((service) => service.slug === slug)!,
  ),
  ...serviceCatalog.filter((service) => !seasonFirst.includes(service.slug)),
];

export const getService = (slug: string) =>
  services.find((s) => s.slug === slug);
